const LABEL_EXPANDED = "閉じる";
const LABEL_COLLAPSED = "開く";
const HEIGHT_TRANSITION_MS = 400;

type ExpandElements = {
  root: HTMLElement;
  clip: HTMLElement;
  content: HTMLElement;
  toggle: HTMLButtonElement;
  label: HTMLElement;
};

let expandId = 0;

function queryExpand(root: HTMLElement): ExpandElements | null {
  const clip = root.querySelector<HTMLElement>("[data-expand-body]");
  const content = root.querySelector<HTMLElement>("[data-expand-content]");
  const toggle = root.querySelector<HTMLButtonElement>("[data-expand-toggle]");
  const label = root.querySelector<HTMLElement>("[data-expand-toggle-label]");

  if (!clip || !content || !toggle || !label) return null;

  return { root, clip, content, toggle, label };
}

function isVisible(element: HTMLElement) {
  return element.getClientRects().length > 0;
}

function collapsedLimit(clip: HTMLElement) {
  const raw = getComputedStyle(clip)
    .getPropertyValue("--expand-collapsed")
    .trim();
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : 0;
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function setLabel(elements: ExpandElements, expanded: boolean) {
  elements.label.textContent = expanded ? LABEL_EXPANDED : LABEL_COLLAPSED;
  elements.toggle.setAttribute("aria-expanded", String(expanded));
}

function nextGeneration(root: HTMLElement) {
  const next = Number(root.dataset.expandGeneration ?? "0") + 1;
  root.dataset.expandGeneration = String(next);
  return next;
}

function afterHeightTransition(
  elements: ExpandElements,
  generation: number,
  onDone: () => void,
) {
  let settled = false;
  const finish = () => {
    if (settled) return;
    settled = true;
    elements.clip.removeEventListener("transitionend", onEnd);
    window.clearTimeout(timer);
    if (elements.root.dataset.expandGeneration !== String(generation)) return;
    onDone();
  };
  const onEnd = (event: TransitionEvent) => {
    if (event.target !== elements.clip || event.propertyName !== "max-height")
      return;
    finish();
  };
  elements.clip.addEventListener("transitionend", onEnd);
  const timer = window.setTimeout(finish, HEIGHT_TRANSITION_MS);
}

function syncExpand(elements: ExpandElements) {
  if (elements.root.dataset.expandAnimating === "true") return;
  if (!isVisible(elements.root)) return;

  const isShort =
    elements.content.scrollHeight <= collapsedLimit(elements.clip) + 1;
  const expanded = elements.root.classList.contains("is-expanded") && !isShort;

  elements.root.classList.toggle("is-short", isShort);
  elements.root.classList.toggle("is-expanded", expanded);
  elements.toggle.hidden = isShort;
  elements.clip.style.maxHeight = expanded ? "none" : "";
  setLabel(elements, expanded);
}

function setExpanded(elements: ExpandElements) {
  if (elements.root.classList.contains("is-short")) return;

  const expanding = !elements.root.classList.contains("is-expanded");
  const limit = collapsedLimit(elements.clip);
  const full = elements.content.scrollHeight;

  if (prefersReducedMotion()) {
    elements.root.classList.toggle("is-expanded", expanding);
    setLabel(elements, expanding);
    elements.clip.style.maxHeight = expanding ? "none" : "";
    return;
  }

  const generation = nextGeneration(elements.root);
  elements.root.dataset.expandAnimating = "true";

  if (expanding) {
    elements.clip.style.maxHeight = `${limit}px`;
    elements.root.classList.add("is-expanded");
    setLabel(elements, true);
    elements.clip.getBoundingClientRect();
    elements.clip.style.maxHeight = `${full}px`;
  } else {
    elements.clip.style.maxHeight = `${full}px`;
    elements.clip.getBoundingClientRect();
    elements.root.classList.remove("is-expanded");
    setLabel(elements, false);
    elements.clip.getBoundingClientRect();
    elements.clip.style.maxHeight = `${limit}px`;
  }

  afterHeightTransition(elements, generation, () => {
    delete elements.root.dataset.expandAnimating;
    elements.clip.style.maxHeight = elements.root.classList.contains(
      "is-expanded",
    )
      ? "none"
      : "";
  });
}

function initProductExpand(root: HTMLElement) {
  const elements = queryExpand(root);
  if (!elements) return;

  expandId += 1;
  if (!elements.clip.id) elements.clip.id = `product-expand-${expandId}`;
  elements.toggle.setAttribute("aria-controls", elements.clip.id);

  setLabel(elements, false);
  syncExpand(elements);

  elements.toggle.addEventListener("click", () => {
    setExpanded(elements);
  });

  const observer = new ResizeObserver(() => {
    syncExpand(elements);
  });
  observer.observe(elements.content);
}

export const expandProductSpecification = () => {
  const roots = document.querySelectorAll<HTMLElement>("[data-product-expand]");

  roots.forEach((root) => {
    initProductExpand(root);
  });

  window.addEventListener("resize", () => {
    roots.forEach((root) => {
      const elements = queryExpand(root);
      if (elements) syncExpand(elements);
    });
  });
};
